import { usePlayFlowState } from '@/stores/playFlow';
import { cloneDeep, last } from 'lodash';
import { AssignAction, AnyEventObject, assign } from 'xstate';
import { TMachineActions } from '@/types';
import type { IMachineContext } from '@/types';
import { useGeneralState } from '@/stores/general';
import { TPlayDesigns, TPlayerRole } from '@/constants';

/**
 * Second param to machine send() func - AnyEventObject definition
 * Event object varies by Action type
 *
 * @param ctxUpdate: Partial<'IMachineContext'> - Context to be updated
 * @param nextStateInput: 'TStateInput' - Data to be set for inputForNextComponent metadata
 */
export function getMachineActions(): {
  [key in TMachineActions]: AssignAction<IMachineContext, AnyEventObject>;
} {
  return {
    [TMachineActions.UPDATE_CONTEX]: assign((_ctx, ev) => ({
      ...ev.ctxUpdate,
    })),
    [TMachineActions.SET_NEXT_STATE_INPUT]: assign((ctx, ev) => ({
      prevStateInputs: [...ctx.prevStateInputs, ctx.inputForNextComponent],
      inputForNextComponent: ev.nextStateInput,
    })),
    // FIXME i think these are duplicate actions?
    [TMachineActions.COPY_PREV_STATE_INPUT]: assign((ctx) => ({
      prevStateInputs: ctx.prevStateInputs.slice(0, ctx.prevStateInputs.length - 1),
      inputForNextComponent: last(ctx.prevStateInputs),
    })),
    [TMachineActions.UNSET_NEXT_STATE_INPUT]: assign((ctx) => {
      const prevInps = cloneDeep(ctx.prevStateInputs);
      const curr = prevInps.pop();
      return {
        prevStateInputs: prevInps,
        inputForNextComponent: curr,
      };
    }),
    [TMachineActions.SAVE_PLAY]: assign((ctx, _ev) => {
      const playFlowState = usePlayFlowState();
      const qbs = useGeneralState().currentGameQuarterbacks;
      const shouldShowQbData =
        !!qbs[playFlowState.presentStateBasedOnCurrentMode.activeTeam] &&
        !ctx.currentPlay.options?.isNonQb1Pass;
      const isQBRush = ctx.currentPlay.qbScramble || ctx.currentPlay.options?.isIntenionalGrounding;

      if (ctx.currentPlay.playDesignId === TPlayDesigns.PASS && !isQBRush) {
        ctx.currentPlay.players = [
          ...(ctx.currentPlay.players || []),
          {
            playerId: shouldShowQbData
              ? qbs[playFlowState.presentStateBasedOnCurrentMode.activeTeam].playerId
              : null,
            roleId: TPlayerRole.PASSER,
          },
        ];
      } else if (isQBRush) {
        ctx.currentPlay.players = [
          {
            playerId: shouldShowQbData
              ? qbs[playFlowState.presentStateBasedOnCurrentMode.activeTeam].playerId
              : null,
            roleId: TPlayerRole.RUSHER,
          },
        ];
      }

      if (!playFlowState.editing && !playFlowState.inserting) {
        playFlowState.pushPlay(useGeneralState().currentGame.gameId, ctx.currentPlay);
      } else if (!playFlowState.inserting) {
        playFlowState.commitPlayEdit(useGeneralState().currentGame.gameId, ctx.currentPlay);
      } else {
        playFlowState.commitPlayInsert(useGeneralState().currentGame.gameId, ctx.currentPlay);
      }

      return {
        currentPlay: null,
      };
    }),
    [TMachineActions.CLEAR_PLAY]: assign<IMachineContext, AnyEventObject>(() => ({
      currentPlay: null,
      currentWorkflow: null,
    })),
    [TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA]: assign<IMachineContext, AnyEventObject>(() => ({
      yardageToggleData: null,
    })),
    [TMachineActions.RESET_CONTEXT]: assign<IMachineContext, AnyEventObject>(() => ({
      currentPlay: null,
      prevStateInputs: [],
      inputForNextComponent: null,
      yardageToggleData: null,
      currentWorkflow: null,
    })),
  };
}
